import axios, { AxiosResponse } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import { Meta, MetaWithoutPagination, Response as ApiResponse } from '@/types/api/response.types';
import {
  ForgotPasswordBody,
  InviteAccept,
  InviteOrgUserBody,
  InviteValid,
  LoginBody,
  LoginResponse,
  RegisterBody,
  ResendInviteData,
  ResetPasswordBody,
  Token,
  UserPermissions,
} from '@/types/api/auth.types';

const baseUrl = (import.meta.env.VITE_APP_API_BASE_URL as string) || '';
const resource = '/auth';

export const login = async (
  data: LoginBody,
): Promise<AxiosResponse<ApiResponse<LoginResponse>>> => {
  return axiosRequest(false).post(`${resource}/login`, data);
};

export const register = async (
  data: RegisterBody,
): Promise<AxiosResponse<ApiResponse<LoginResponse>>> => {
  return axiosRequest(false).post(`${resource}/register`, data);
};

export const emailConfirm = async (
  code: string,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest(false).post(`${resource}/verifications/emails/confirmation`, { code });
};

export const resendConfirmation = async (
  email: string,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest(false).post(`${resource}/verifications/emails/resend`, { email });
};

export const invitationValid = async (
  code: string,
): Promise<AxiosResponse<ApiResponse<InviteValid>>> => {
  return axiosRequest(false).get(`${resource}/invitations/${code}`);
};

export const invitationConfirm = async (
  data: InviteAccept,
): Promise<AxiosResponse<ApiResponse<LoginResponse>>> => {
  return axiosRequest(false).post(`${resource}/invitations/confirmation`, data);
};

export const resendInvitation = async (
  data: ResendInviteData,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest().post(`${resource}/invitations/resend`, data);
};

export const forgotPassword = async (
  email: ForgotPasswordBody,
): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest(false).post(`${resource}/forgot-password`, email);
};

export const resetTokenValid = async (token: string): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest(false).get(`${resource}/forgot-password/${token}`);
};

export const resetPassword = async (
  data: ResetPasswordBody,
): Promise<AxiosResponse<ApiResponse<Meta>>> => {
  return axiosRequest(false).post(`${resource}/reset-password`, data);
};

export const getCurrentUserPermissions = async (): Promise<
  AxiosResponse<ApiResponse<UserPermissions[]>>
> => {
  return axiosRequest().get(`${resource}/roles/permissions`);
};

export const inviteUserToOrg = async (
  data: InviteOrgUserBody,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest().post(`${resource}/invitations`, data);
};

/**
 * This is a special case where we need to refresh the access token
 * without using the axios instance. This is because the axios instance
 * will contain logic to refresh the access token if it is expired,
 * thus causing a recursive call that will cause a premature logout.
 */
export const refresh = async (refreshToken: string): Promise<AxiosResponse<ApiResponse<Token>>> => {
  return axios.post(
    `${baseUrl}${resource}/refresh-token`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json; charset=utf-8' } },
  );
};
